import {
  IAjuste,
  IEventoDoFechamento,
  IMesDaEmpresa,
  ISituacaoDoFechamento,
  Marca,
  TipoSemMovimento,
} from '../../../../hooks/useFechamentoContabilMensal';
import { aparenciaDoParecer, COR } from './regras';
import { dataLonga } from './regrasDoDetalhe';

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/** Texto curto da pendência do arquivo, na régua, no card e na barra do fechamento. */
export const TXT_PENDENTE = 'TXT pendente para o Domínio';

export const SEM_MOVIMENTO: Record<TipoSemMovimento, string> = {
  sem_movimento: 'Sem movimento no exercício',
  mes_parado: 'Sem movimento no mês',
};

/** Os filtros do fechamento na carteira, na ordem em que aparecem. */
export const MARCAS: {
  chave: Exclude<Marca, ''>;
  rotulo: string;
  dica: string;
  cor: string;
  suave: string;
}[] = [
  {
    chave: 'sem_movimento_exercicio',
    rotulo: 'Sem movimento no exercício',
    dica: 'Nenhum lançamento, nota, folha ou extrato no mês nem no exercício. O sistema fecha sozinho, em ordem, depois do mês anterior fechado.',
    cor: COR.apagado,
    suave: COR.neutroSuave,
  },
  {
    chave: 'sem_movimento_mes',
    rotulo: 'Sem movimento no mês',
    dica: 'O mês parou, mas a empresa operou em outros meses do exercício. O sistema fecha sozinho, em ordem, depois do mês anterior fechado.',
    cor: COR.apagado,
    suave: COR.neutroSuave,
  },
  {
    chave: 'fechadas',
    rotulo: 'Fechadas',
    dica: 'Competências fechadas, pela área (inclusive as encerradas no Domínio e as entregues no Acessórias) ou pelo sistema.',
    cor: COR.ok,
    suave: COR.okSuave,
  },
  {
    chave: 'fechadas_pelo_sistema',
    rotulo: 'Fechadas pelo sistema',
    dica: 'Fechadas automaticamente por falta de movimento.',
    cor: COR.ok,
    suave: COR.okSuave,
  },
  {
    chave: 'sistema_proprio',
    rotulo: 'Sistema próprio',
    dica: 'Fecham no sistema do cliente (BRDATA, Sankhya): aqui o fechamento é só controle.',
    cor: COR.andamento,
    suave: COR.andamentoSuave,
  },
];

/**
 * O que ainda está em aberto na competência. Fechar é sempre permitido; esta lista
 * é o aviso que a pessoa lê antes de confirmar. Ajuste não tem decisão (16/10/2026):
 * o que estiver proposto na hora de fechar vai para o arquivo do Domínio.
 */
export const oQueEstaAberto = (s: ISituacaoDoFechamento): string[] => {
  const aberto: string[] = [];
  if (s.parecer !== 'liberar' && s.parecer !== 'sem_movimento') {
    aberto.push(`Parecer da revisão: ${aparenciaDoParecer(s.parecer).rotulo.toLowerCase()}`);
  }
  if (s.criticas) aberto.push(plural(s.criticas, 'constatação crítica', 'constatações críticas'));
  if (s.altas) aberto.push(plural(s.altas, 'constatação alta', 'constatações altas'));
  return aberto;
};

/** Entra no próximo arquivo: completo para o Domínio e ainda não exportado. */
export const vaiNoArquivo = (ajuste: IAjuste): boolean =>
  ajuste.situacao !== 'RECUSADO' && !ajuste.motivo_fora && !ajuste.exportado_em;

export const dataEHora = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return dataLonga(iso);
  const hora = quando.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${quando.toLocaleDateString('pt-BR')} às ${hora}`;
};

const quem = (e: IEventoDoFechamento) => e.nome || e.por;

/** Como a competência foi fechada: "Fechada por Ana em ..." ou "Fechada pelo sistema: ...". */
export const comoFoiFechada = (e: IEventoDoFechamento): string => {
  if (e.origem === 'sistema') {
    const tipo = e.tipo ? SEM_MOVIMENTO[e.tipo].toLowerCase() : 'sem movimento';
    return `Fechada pelo sistema: ${tipo}, em ${dataEHora(e.em)}`;
  }
  if (e.origem === 'dominio') {
    const base = `Fechada pela área no Domínio, registrada em ${dataEHora(e.em)}`;
    return e.com_pendencia ? `${base} · com pendência aberta na revisão` : base;
  }
  if (e.origem === 'acessorias') {
    const entrega = e.entregue_em ? ` em ${dataLonga(e.entregue_em)}` : '';
    const por = e.responsavel ? ` por ${e.responsavel}` : '';
    const base = `Entregue pela área no Acessórias${entrega}${por}, importada em ${dataEHora(e.em)}`;
    return e.com_pendencia ? `${base} · com pendência aberta na revisão` : base;
  }
  return `Fechada por ${quem(e)} em ${dataEHora(e.em)}`;
};

/** O que a régua de meses e o card dizem do fechamento do mês, depois do parecer. */
export const marcaDoMes = (m: IMesDaEmpresa): string => {
  const partes: string[] = [];
  if (m.sem_movimento) partes.push(SEM_MOVIMENTO[m.sem_movimento].toLowerCase());
  if (m.fechada) {
    partes.push(m.fechada_pelo_sistema ? 'fechada pelo sistema' : 'fechada pela área');
    if (m.com_pendencia) partes.push('com pendência');
    if (m.alterada) partes.push('alterada depois');
  }
  return partes.map(p => ` · ${p}`).join('');
};

/** Por que o mês está travado: o motivo da etapa de fechamento, só nos meses "Bloquear". */
export const motivoDaTrava = (m: IMesDaEmpresa): string =>
  m.status === 'bloquear' ? (m.etapas?.consolidacao?.motivo ?? '') : '';

/**
 * A trava que o card mostra: a do mês travado mais recente. Sem ela, o card vermelho
 * com "0 crít." não dizia o motivo (caso real: receita sem nota fiscal).
 */
export const travaDaEmpresa = (
  meses: IMesDaEmpresa[],
): { competencia: string; motivo: string } | null => {
  const mes = [...meses].reverse().find(m => motivoDaTrava(m));
  return mes ? { competencia: mes.competencia, motivo: motivoDaTrava(mes) } : null;
};

/** Uma linha do histórico: quem, quando e por quê. */
export const textoDoEvento = (e: IEventoDoFechamento): string => {
  if (e.evento === 'txt_gerado') {
    const n = e.incluidos ?? 0;
    const base = `TXT gerado por ${quem(e)} em ${dataEHora(e.em)}, ${plural(n, 'lançamento', 'lançamentos')}`;
    return e.reexportado ? `${base} (incluindo os já exportados)` : base;
  }
  if (e.evento === 'importacao_cancelada') {
    const base = `Importação do Acessórias cancelada por ${quem(e)} em ${dataEHora(e.em)}: volta a aberta`;
    return e.motivo ? `${base} (${e.motivo})` : base;
  }
  if (e.evento === 'fechado') {
    const base = comoFoiFechada(e);
    return e.justificativa ? `${base}: ${e.justificativa}` : base;
  }
  const por = e.origem === 'sistema' ? 'pelo sistema' : `por ${quem(e)}`;
  const base = `Reaberta ${por} em ${dataEHora(e.em)}`;
  return e.motivo ? `${base}: ${e.motivo}` : base;
};

/**
 * O Domínio (desktop Windows) lê o .txt como ANSI/Windows-1252. Acentos do
 * português batem 1:1 com o Latin-1; o que está fora dele vira '?'. É a mesma
 * conversão do Exportar do ContaCil.
 */
export const emLatin1 = (texto: string): Uint8Array => {
  const bytes = new Uint8Array(texto.length);
  for (let i = 0; i < texto.length; i += 1) {
    const codigo = texto.charCodeAt(i);
    bytes[i] = codigo <= 0xff ? codigo : 0x3f;
  }
  return bytes;
};

export const baixarTxt = (conteudo: string, nome: string): void => {
  const blob = new Blob([emLatin1(conteudo)], { type: 'text/plain;charset=windows-1252' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
